import React from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from 'antd';
import { CaretUpOutlined, CaretDownOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getUserInfo } from '../../../../utils/auth';

dayjs.extend(utc);
dayjs.extend(timezone);

interface Ranking {
  rank: number;
  totalWarehouses: number;
  weekOverWeekChange?: number | null;
  pointsBehindNext?: number | null;
  nextWarehouseName?: string | null;
  networkPercentileText?: string | null;
}

interface ShiftBannerProps {
  data: {
    warehouseName: string;
    timeZone?: string | null;
    serverNow?: string | null;
    // 全 4 类任务口径，与任务栏（仅 DO 两类）的计数不通用；上游未给汇总时为空，整块不渲染
    todayTasks?: {
      doneCount: number;
      totalCount: number;
      completionPercent: number;
    } | null;
    ranking?: Ranking | null;
    deadlineLabel?: string | null;
    pendingCount?: number;
  };
  loading?: boolean;
}

// 「网络前 20%」→ 20，用于排名卡进度条；解析失败时不渲染进度条
const parsePercentile = (text?: string | null): number | null => {
  if (!text) return null;
  const matched = text.match(/(\d+(?:\.\d+)?)\s*%/);
  if (!matched) return null;
  const value = Number(matched[1]);
  return Number.isFinite(value) ? value : null;
};

const RankingCard: React.FC<{ ranking: Ranking }> = ({ ranking }) => {
  const { t } = useTranslation();
  const change = ranking.weekOverWeekChange;
  const hasChange = change !== null && change !== undefined && change !== 0;
  const isUp = (change ?? 0) > 0;
  const percentile = parsePercentile(ranking.networkPercentileText);

  return (
    <div className="bg-gradient-to-r from-violet-600 to-purple-700 rounded-xl px-4 py-3 text-white flex items-center gap-4">
      <div className="shrink-0 w-14 h-14 rounded-full bg-white/20 flex flex-col items-center justify-center leading-none">
        <span className="text-lg font-bold">#{ranking.rank}</span>
        <span className="text-[10px] opacity-90 mt-0.5">
          {String(t('workbench.ranking.totalWarehouses', { count: ranking.totalWarehouses }))}
        </span>
      </div>

      <div className="min-w-0 flex-1">
        <p className="text-xs opacity-90">{String(t('workbench.ranking.title'))}</p>
        {hasChange && (
          <p className="text-sm font-semibold flex items-center gap-1">
            {isUp ? <CaretUpOutlined /> : <CaretDownOutlined />}
            {String(
              t(isUp ? 'workbench.ranking.weeklyUp' : 'workbench.ranking.weeklyDown', {
                count: Math.abs(change ?? 0),
              })
            )}
          </p>
        )}
        {ranking.pointsBehindNext !== null && ranking.pointsBehindNext !== undefined && (
          <p className="text-xs opacity-90 truncate">
            {String(
              t('workbench.ranking.behindNext', {
                rank: ranking.rank - 1,
                points: ranking.pointsBehindNext,
              })
            )}
            {ranking.nextWarehouseName ? ` · ${ranking.nextWarehouseName}` : ''}
          </p>
        )}
      </div>

      {percentile !== null && (
        <div className="shrink-0 w-24">
          <div className="h-1.5 rounded-full bg-white/25 overflow-hidden">
            <div className="h-full rounded-full bg-lime-300" style={{ width: `${percentile}%` }} />
          </div>
          <p className="text-[11px] opacity-90 mt-1 text-right">{ranking.networkPercentileText}</p>
        </div>
      )}
    </div>
  );
};

const ShiftBanner: React.FC<ShiftBannerProps> = ({ data, loading }) => {
  const { t } = useTranslation();

  if (loading) {
    return <Skeleton active paragraph={{ rows: 2 }} className="mb-4" />;
  }

  // 以服务端 serverNow 换算到仓所在时区，不取客户端时钟：仓管看的是仓的「现在几点」，
  // 与浏览器所在地无关。随数据轮询刷新，不做秒级走动。
  // 上游未给时区时整条日期时间降级为 —：回落浏览器时钟会把「时间错了」伪装成「时间对了」
  const localNow = data.serverNow && data.timeZone
    ? dayjs(data.serverNow).tz(data.timeZone)
    : null;
  const weekdays = t('workbench.weekdays', { returnObjects: true }) as unknown as string[];
  const weekdayText = localNow && Array.isArray(weekdays) ? weekdays[localNow.day()] : '';
  const dateText = localNow ? localNow.format(String(t('workbench.dateFormat'))) : '—';
  const timeText = localNow ? localNow.format('HH:mm') : '—';

  const hour = localNow?.hour();
  const greetingKey =
    hour === undefined ? 'workbench.greeting.hello'
      : hour < 12 ? 'workbench.greeting.morning'
        : hour < 18 ? 'workbench.greeting.afternoon'
          : 'workbench.greeting.evening';

  const userName = getUserInfo()?.account?.name ?? '';
  const pendingCount = data.pendingCount ?? 0;

  return (
    <div className="bg-gradient-to-r from-violet-50 to-purple-50 px-6 py-4 border-b border-violet-200">
      <div className="flex items-center justify-between gap-6">
        {/* 左：日期 + 时间 + 问候语 */}
        <div className="min-w-0">
          <p className="text-xs text-gray-500">
            {dateText} {weekdayText} · {timeText}
          </p>
          <h2 className="text-xl font-bold text-gray-900 mt-1 truncate">
            {String(t(greetingKey, { name: userName }))}
            {data.deadlineLabel && pendingCount > 0 && (
              <>
                {' —— '}
                {String(t('workbench.pendingBeforeDeadline', {
                  deadline: data.deadlineLabel,
                  count: pendingCount,
                }))}
              </>
            )}
          </h2>
        </div>

        {/* 中：今日任务 */}
        {data.todayTasks && (
          <div className="shrink-0 text-right">
            <p className="text-xs text-gray-500">{String(t('workbench.todayTasks'))}</p>
            <p className="text-xl font-bold text-gray-900">
              {data.todayTasks.doneCount} / {data.todayTasks.totalCount} ·{' '}
              {data.todayTasks.completionPercent}%
            </p>
            <div className="h-1.5 w-40 rounded-full bg-gray-200 overflow-hidden mt-1">
              <div
                className="h-full rounded-full bg-emerald-500"
                style={{ width: `${data.todayTasks.completionPercent}%` }}
              />
            </div>
          </div>
        )}

        {/* 右：仓库排名 */}
        {data.ranking && (
          <div className="shrink-0">
            <RankingCard ranking={data.ranking} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ShiftBanner;
